import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Menu } from 'src/app/types';
import { Images } from 'src/app/shared/constants/ImageConstants';
import { GetLocalStorageValues } from 'src/app/shared/util/local-storage';
import { PermissionService, MenuNode } from 'src/app/core/services/permission.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly ls = new GetLocalStorageValues();
  private readonly permissionService = inject(PermissionService);

  private menusSubject = new BehaviorSubject<Menu[]>([]);
  public menus$ = this.menusSubject.asObservable().pipe(
    map(menus => menus.filter((m: any) => !m.hidden))
  );

  private currentRoleType: string = '2';
  private isBookingAllowed: boolean = false;

  private defaultMenus: Menu[] = [
    {
      name: 'Dashboard',
      icon: `${Images.Dashboard}`,
      padding: '5.2rem',
      route: 'main/dashboard',
      subMenu: [],
    },
    {
      name: 'Competitions',
      icon: `${Images.Competitions}`,
      padding: '3.5rem',
      route: 'main/competitions',
      category: 'Activities',
      subMenu: [],
    },
    {
      name: 'Federation Leagues',
      icon: `${Images.Permissions}`,
      padding: '3.5rem',
      route: 'main/federation',
      category: 'Activities',
      subMenu: [],
    },
    {
      name: 'Sessions',
      icon: `${Images.Session}`,
      padding: '6.8rem',
      route: 'main/report/session',
      category: 'Activities',
      subMenu: [],
    },
    {
      name: 'Holiday Camps',
      icon: `${Images.HolidayCamp}`,
      padding: '4rem',
      route: 'main/report/holidaycamp',
      category: 'Activities',
      subMenu: [],
    },
    {
      name: 'Events',
      icon: `${Images.Events}`,
      padding: '7.4rem',
      route: 'main/report/events',
      category: 'Activities',
      subMenu: [],
    },
    {
      name: 'School Sessions',
      icon: `${Images.SchoolSession}`,
      padding: '3.1rem',
      route: 'main/report/schoolsession',
      category: 'Activities',
      subMenu: [],
    },
    {
      name: 'Bookings',
      icon: `${Images.CourtBooking}`,
      padding: '6.2rem',
      route: 'main/court-booking',
      category: 'Bookings',
      subMenu: [],
    },
    {
      name: 'Food & Beverage',
      icon: `${Images.Shop}`,
      padding: '3rem',
      route: 'main/fnb',
      category: 'Bookings',
      subMenu: [],
    },
    {
      name: 'Members',
      icon: `${Images.Member}`,
      padding: '6.2rem',
      route: 'main/member/member-detail',
      category: 'Members',
      subMenu: [],
    },
    {
      name: 'Membership',
      icon: `${Images.Membership}`,
      isClicked: false,
      padding: '5.4rem',
      route: 'main/membership-record',
      category: 'Members',
      subMenu: [],
      hidden: false,
    },
    {
      name: 'Staff Management',
      icon: `${Images.Membership}`,
      isClicked: false,
      padding: '5.4rem',
      route: 'main/staff-attendance',
      category: 'Members',
      subMenu: [],
      hidden: false,
    },
    {
      name: 'Email',
      icon: `${Images.Email}`,
      padding: '7.5rem',
      route: 'main/email/compose',
      category: 'Members',
      subMenu: [],
    },
    {
      name: 'Polls',
      icon: `${Images.Polls}`,
      padding: '7.5rem',
      route: 'main/polls',
      category: 'Members',
      subMenu: [],
    },
    {
      name: 'Notification',
      icon: `${Images.Notification}`,
      padding: '7.5rem',
      route: 'main/notification-management',
      category: 'Members',
      subMenu: [],
    },
    {
      name: 'Shop',
      icon: `${Images.Shop}`,
      isClicked: false,
      padding: '6.7rem',
      category: 'Commerce',
      subMenu: [
        {
          name: 'Manage Orders',
          icon: `${Images.ManageOrders}`,
          route: 'main/shop/manage-orders',
        },
        {
          name: 'Inventory',
          icon: `${Images.Inventory}`,
          route: 'main/shop/inventory',
        },
      ],
    },
    {
      name: 'Reports',
      icon: `${Images.Report}`,
      isClicked: false,
      padding: '6.3rem',
      category: 'Finance',
      subMenu: [
        { name: 'Member', icon: `${Images.Member}`, route: 'main/report/member' },
        {
          name: 'Facility Booking',
          icon: `${Images.CourtBooking}`,
          route: 'main/report/facility-booking-report',
        },
      ],
    },
    {
      name: 'Payments',
      icon: `${Images.PaymentReport}`,
      isClicked: false,
      padding: '5.4rem',
      route: 'main/payment-report',
      category: 'Finance',
      subMenu: [],
    },
    {
      name: 'Wallet',
      icon: `${Images.Wallet}`,
      isClicked: false,
      padding: '5.4rem',
      route: 'main/wallet-report',
      category: 'Finance',
      subMenu: [],
    },
    {
      name: 'Membership',
      icon: `${Images.Membership}`,
      isClicked: false,
      padding: '5.4rem',
      route: 'main/membership-report',
      category: 'Finance',
      subMenu: [],
      hidden: false,
    },
    {
      name: 'Setup',
      icon: `${Images.SetUp}`,
      isClicked: false,
      padding: '6.7rem',
      route: 'main/setup',
      category: 'Admin',
      subMenu: [],
    },
    {
      name: 'Help & Feedback',
      icon: `${Images.HelpFeedback}`,
      isClicked: false,
      padding: '3rem',
      route: 'main/cases',
      category: 'Support',
      subMenu: [],
    },
  ];

  // Super user menus
  private superUserMenus: Menu[] = [
    // {
    //   name: 'Menu Manager',
    //   icon: `${Images.ManageMenu}`,
    //   padding: '4rem',
    //   route: 'super/menu-management',
    //   subMenu: [],
    // },
  ];

  constructor() {
    this.menusSubject.next(this.defaultMenus);
  }

  setRoleType(roleType: string, isBookingAllowed: boolean = false): void {
    this.currentRoleType = roleType;
    this.isBookingAllowed = isBookingAllowed;
    this.updateMenusBasedOnRole();
    // For non-admin roles with permission menu enabled, trigger async permission load
    // Skip for super users who haven't selected a club yet
    const isSuperUser = localStorage.getItem('superUser') === 'true';
    const hasSelectedClub = this.ls.getPostgreParentClubId() !== '';
    if (environment.usePermissionMenu && roleType !== '2' && !(isSuperUser && !hasSelectedClub)) {
      this.refreshMenus();
    }
  }

  private updateMenusBasedOnRole(): void {
    const isSuperUser = localStorage.getItem('superUser') === 'true';
    const hasSelectedClub = this.ls.getPostgreParentClubId() !== '';

    if (isSuperUser && !hasSelectedClub) {
      this.menusSubject.next([...this.superUserMenus]);
      return;
    }

    if (isSuperUser && hasSelectedClub) {
      this.menusSubject.next(this.applyPermissionFilter([...this.defaultMenus]));
      return;
    }

    if (this.currentRoleType !== '2') {
      // Non-admin roles: show permission-filtered menus (or booking-only fallback if permissions not active)
      if (environment.usePermissionMenu) {
        // Permission system enabled: show all menus (filter applied once permissions load via refreshMenus)
        this.menusSubject.next(this.applyPermissionFilter([...this.defaultMenus]));
      } else if (this.isBookingAllowed) {
        const bookingMenu = this.defaultMenus.find(menu => menu.name === 'Booking');
        if (bookingMenu) {
          this.menusSubject.next([bookingMenu]);
        } else {
          this.menusSubject.next([]);
        }
      } else {
        this.menusSubject.next([]);
      }
    } else {
      this.menusSubject.next(this.applyPermissionFilter([...this.defaultMenus]));
    }
  }

  /**
   * Filter menus by permission service (when feature flag is on).
   * If feature flag is off or permissions not loaded, returns menus unchanged.
   */
  private applyPermissionFilter(menus: Menu[]): Menu[] {
    if (!environment.usePermissionMenu || !this.permissionService.isActive()) {
      return menus;
    }


    // Get all permitted routes from the permission tree
    const permittedRoutes = new Set<string>();
    this.collectRoutes(this.permissionService.getMenuTree(), permittedRoutes);

    return menus.map(m => ({ ...m, subMenu: [...(m.subMenu || [])] })).filter(menu => {
      // If menu has a route, it must be in the permitted set
      if (menu.route && !permittedRoutes.has(menu.route)) {
        return false;
      }
      // If menu has sub-menus, filter them too
      if (menu.subMenu?.length) {
        menu.subMenu = menu.subMenu.filter(sub => {
          return !sub.route || permittedRoutes.has(sub.route);
        });
        // If a parent menu has no route and all sub-menus filtered out, hide it
        if (!menu.route && menu.subMenu.length === 0) return false;
      }
      return true;
    });
  }

  private collectRoutes(nodes: MenuNode[], routes: Set<string>): void {
    for (const node of nodes) {
      if (node.route && node.has_access) {
        routes.add(node.route);
      }
      if (node.children?.length) {
        this.collectRoutes(node.children, routes);
      }
    }
  }

  switchToSuperUserMenus(): void {
    this.menusSubject.next([...this.superUserMenus]);
  }

  switchToRegularMenus(): void {
    this.updateMenusBasedOnRole();
  }

  /** Re-evaluate menus after permissions are loaded */
  refreshMenus(): void {
    if (environment.usePermissionMenu && this.permissionService.isActive()) {
      this.menusSubject.next(this.applyPermissionFilter([...this.defaultMenus]));
    } else if (environment.usePermissionMenu && !this.permissionService.isActive()) {
      // Permissions not loaded yet — load them now
      const user = JSON.parse(localStorage.getItem('USER') || '{}');
      const roleType = Number(user.roleType) || 2;
      this.permissionService.loadPermissions(roleType).then(() => {
        this.menusSubject.next(this.applyPermissionFilter([...this.defaultMenus]));
      });
    } else {
      this.updateMenusBasedOnRole();
    }
  }

  getMenus(): Menu[] {
    return this.menusSubject.value.filter((m: any) => !m.hidden);
  }

  resetMenuStates(): void {
    const resetMenus = this.getMenus().map((menu) => ({
      ...menu,
      isClicked: false,
    }));
    this.menusSubject.next(resetMenus);
  }
}
